export default function AboutValaxis() {
  return (
    <section id="about-valaxis" className="py-16 md:py-[110px]">
      <div className="max-w-[1120px] mx-auto px-5 md:px-10">

        {/* Section Header */}
        <h2 className="text-[24px] md:text-[30px] font-semibold text-[#1E2430] mb-8 md:mb-[48px]">
          About Valaxis Group
        </h2>

        {/* Narrative Block */}
        <div className="flex flex-col lg:flex-row items-start gap-10 lg:gap-16 mb-12 md:mb-[72px]">
          <div className="w-full lg:max-w-[620px]">
            <p className="text-[16px] md:text-[17px] leading-[1.75] text-[#5B6475] mb-6">
              Valaxis Group was founded on the conviction that building stronger healthcare sector and technology-enabled businesses contributes to a more effective and resilient healthcare ecosystem.
            </p>
            <p className="text-[16px] md:text-[17px] leading-[1.75] text-[#5B6475] mb-6">
              The firm&apos;s strategy is rooted in deep executive leadership experience across CEO roles, acquisitions, and exits, with a consistent focus on disciplined commercial execution, operational excellence, and long-term value creation.
            </p>
            <p className="text-[16px] md:text-[17px] leading-[1.75] text-[#5B6475]">
              By acquiring and scaling organizations that deliver meaningful value to healthcare stakeholders and patients, Valaxis Group seeks to create enduring enterprise value while aligning the interests of owners, management teams, and investors.
            </p>
          </div>
          <div className="hidden lg:flex items-center justify-center flex-shrink-0">
            <img
              src="https://static.readdy.ai/image/cd71ffc29b4128737d19646fb970e806/23c6485507286e2714b90e8bd3ae7df8.png"
              alt="Valaxis Group logo"
              title="Valaxis Group"
              className="w-[260px] h-auto object-contain opacity-80"
            />
          </div>
        </div>

        {/* Thin rule separator */}
        <div className="h-px bg-[rgba(30,36,48,0.06)] mb-6" />

        {/* Values */}
        <h3 className="text-[20px] md:text-[24px] font-semibold text-[#1E2430] mb-6">
          Values
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-[28px] mb-6">
          {[
            { title: 'Integrity', desc: 'Operate with transparency, fairness, and intellectual honesty.' },
            { title: 'Accountability', desc: 'Take responsibility for outcomes, decisions, and commitments.' },
            { title: 'People', desc: 'Build value by creating strong teams and lasting customer relationships.' },
            { title: 'Commitment', desc: 'Pursue long-term partnerships and disciplined execution.' },
            { title: 'Respect', desc: 'Value founders, colleagues, customers, partners, investors and stakeholders.' },
            { title: 'On-Going Learning', desc: 'Continuously evolve our thinking, processes, and capabilities.' },
          ].map((v) => (
            <div key={v.title} className="bg-white rounded-[14px] p-6 md:p-[36px] border border-[rgba(30,36,48,0.08)]">
              <h4 className="text-[17px] md:text-[20px] font-semibold text-[#1E2430] mb-2 md:mb-3">{v.title}</h4>
              <p className="text-[15px] md:text-[17px] leading-[1.65] text-[#5B6475]">{v.desc}</p>
            </div>
          ))}
        </div>

        {/* Thin rule separator */}
        <div className="h-px bg-[rgba(30,36,48,0.06)] mb-6" />

        {/* Founder Block */}
        <h3 id="founder" className="text-[20px] md:text-[24px] font-semibold text-[#1E2430] mb-6 md:mb-[32px]">
          Founder - Steve Cagle, MBA, CHISL, CDH-E
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8 lg:gap-14 items-start mb-10 md:mb-[48px]">

          {/* Portrait */}
          <div>
            <div className="w-full max-w-[280px] h-[300px] md:h-[340px] rounded-[14px] overflow-hidden bg-[#F6F8FB]">
              <img
                src="https://static.readdy.ai/image/cd71ffc29b4128737d19646fb970e806/79f4fd1ca768f24be0af99b69baf22e4.jpeg"
                alt="Steve Cagle, Founder and CEO of Valaxis Group"
                title="Steve Cagle - Valaxis Group Founder"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <h3 className="text-[20px] md:text-[22px] font-semibold text-[#1E2430] mt-4 mb-1">
              Steve Cagle
            </h3>
            <p className="text-[15px] text-[#5B6475]">
              Founder &amp; CEO
            </p>
            <a
              href="https://www.linkedin.com/in/steve-cagle/"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center gap-1.5 mt-2 text-[14px] text-[#0A66C2] hover:underline cursor-pointer"
            >
              <i className="ri-linkedin-box-fill text-[18px]"></i>
              LinkedIn
            </a>
          </div>

          {/* Bio narrative */}
          <div className="pt-0 lg:pt-1">
            <p className="text-[16px] md:text-[17px] leading-[1.75] text-[#5B6475] mb-6">
              Valaxis Group was founded by Steve Cagle, an experienced healthcare, pharma and technology executive with a track record of leading, scaling, and acquiring businesses across multiple CEO roles.
            </p>
            <p className="text-[16px] md:text-[17px] leading-[1.75] text-[#5B6475] mb-6">
              Throughout his career, Steve has partnered with founders, investors, and management teams to drive growth, execute acquisitions, and build enduring enterprise value. His experience spans quality and regulatory software, cybersecurity and IT services, technology-enabled professional services, pharmaceutical development and commercialization, and regulated environments, with a consistent focus on operational excellence, strategic clarity, and disciplined execution.
            </p>
            <p className="text-[16px] md:text-[17px] leading-[1.75] text-[#5B6475]">
              Steve&apos;s leadership perspective is shaped by decades of executive operating experience, emphasizing long-term value creation, organizational strength, and pragmatic decision-making.
            </p>
          </div>
        </div>

        {/* Selected Leadership Experience */}
        <div>
          <h3 className="text-[20px] md:text-[24px] font-semibold text-[#1E2430] mb-6">
            Selected Leadership Experience
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-[18px]">
            {[
              {
                logo: 'https://static.readdy.ai/image/cd71ffc29b4128737d19646fb970e806/83f8f222f4f5b0bc559d2dffafe81f6f.png',
                alt: 'Clearwater',
                role: 'Chief Executive Officer',
                desc: 'Scaled technology-enabled healthcare services platform through market repositioning, organic growth and acquisitions, resulting in\u00a0700% revenue growth\u00a0and successful exit.',
              },
              {
                logo: 'https://static.readdy.ai/image/cd71ffc29b4128737d19646fb970e806/72e119245d4935221cb4416be2f4d174.png',
                alt: 'Sparta Systems',
                role: 'Executive Leadership',
                desc: 'Early leadership team member contributing to commercial and operational scale within a high-growth life sciences software platform leading to 100X revenue growth during tenure.',
              },
              {
                logo: 'https://static.readdy.ai/image/cd71ffc29b4128737d19646fb970e806/1d200058bebee54bac09455ba3850b59.png',
                alt: 'CMP Pharma',
                role: 'Executive Chairman / Operating Executive',
                desc: 'Drove business transformation, strengthening operating infrastructure and supporting the evolution from founder-led organization to institutionally managed enterprise.',
              },
              {
                logo: 'https://static.readdy.ai/image/cd71ffc29b4128737d19646fb970e806/8a03cf29a755a0b789475d910d9dd04c.png',
                alt: 'Alterna / Moberg Pharma North America',
                role: 'Chief Executive Officer',
                desc: 'Led organizational turnaround and growth strategy, driving operational improvements and enterprise value creation culminating in successful exit to a strategic buyer.',
              },
            ].map((item) => (
              <div key={item.alt} className="bg-white rounded-[14px] p-6 md:p-[32px] border border-[rgba(30,36,48,0.08)]">
                <div className="mb-4 h-[72px] md:h-[84px] flex items-center">
                  <img src={item.logo} alt={item.alt} title={item.alt} className="h-[60px] md:h-[76px] w-auto object-contain" />
                </div>
                <p className="text-[13px] md:text-[14px] font-medium text-[#2E3A59] mb-2 md:mb-[10px]">{item.role}</p>
                <p className="text-[14px] md:text-[15px] leading-[1.5] text-[#5B6475]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}